/**
 * Tiptap doc → StructuredContent serializer.
 *
 * Phase 6: walks the editor's free-flow node tree (no atoms,
 * everything contenteditable) and reassembles a StructuredContent
 * object so edits flow back through the existing autosave path.
 *
 * Tree shape (matches the seeder in TiptapStructuredEditor):
 *
 *   meetingHeader {project, meetingDate, ..., format, variant}
 *     └─ headerTitle [text*]
 *   attendeesBlock
 *     └─ attendeeGroup* {isOjb}
 *          ├─ firmText [text*]
 *          └─ membersText [text*]   (semicolon-separated; commas
 *                                    inside a member entry are
 *                                    reserved for credentials,
 *                                    e.g. "Andrew Cridlin, ASLA")
 *   actionItemsBlock
 *     └─ actionItem* {assigneeName, dueDate, status} [text*]
 *   topic+
 *     ├─ topicTitle [text*]
 *     └─ bullet+ {speaker, isDirectQuote, ambiguityFlag} [text*]
 *
 * The serializer is defensive — any field the doc doesn't carry
 * falls back to the caller-provided baseline (the last-known-good
 * structured_content). Empty bullets get filtered so the schema
 * stays valid; topics with empty titles fall back to "Untitled
 * topic" rather than being dropped (we'd rather show a stub than
 * silently lose user content).
 */
import type { JSONContent } from '@tiptap/core';
import type {
  ActionItem,
  AttendeeGroup,
  Bullet,
  StructuredContent,
  Topic,
} from '@ojb/shared';

type PMNode = JSONContent;

/** Concatenate all text nodes inside a PM node (recursively, in
 *  document order). Marks on text nodes (bold/italic) collapse to
 *  the bare characters — Phase 6 doesn't preserve formatting in
 *  the serialized JSON because StructuredContent is plain strings. */
function nodeText(n: PMNode | undefined | null): string {
  if (!n) return '';
  if (n.type === 'text') return n.text ?? '';
  if (!n.content) return '';
  let out = '';
  for (const child of n.content) out += nodeText(child);
  return out;
}

/** First descendant of `n` whose type matches `type`. */
function findChild(n: PMNode | undefined, type: string): PMNode | undefined {
  if (!n?.content) return undefined;
  for (const c of n.content) if (c.type === type) return c;
  return undefined;
}

function topicKindFromAttr(raw: unknown): Topic['kind'] {
  if (
    raw === 'discussion' ||
    raw === 'action_items' ||
    raw === 'decisions' ||
    raw === 'notes'
  ) {
    return raw;
  }
  return 'discussion';
}

function meetingFormatFromAttr(
  raw: unknown,
): StructuredContent['meetingFormat'] {
  if (
    raw === 'in_person' ||
    raw === 'video' ||
    raw === 'phone' ||
    raw === 'hybrid'
  ) {
    return raw;
  }
  return 'in_person';
}

function templateVariantFromAttr(
  raw: unknown,
): StructuredContent['templateVariant'] {
  if (raw === 'client_meeting' || raw === 'internal') return raw;
  return 'client_meeting';
}

function statusFromAttr(raw: unknown): NonNullable<ActionItem['status']> {
  if (raw === 'open' || raw === 'done' || raw === 'cancelled') return raw;
  return 'open';
}

function bulletFromNode(n: PMNode): Bullet | null {
  const body = nodeText(n).trim();
  if (!body) return null;

  const attrs = n.attrs ?? {};
  const bullet: Bullet = {
    body,
    isDirectQuote: attrs.isDirectQuote === true,
  };
  if (typeof attrs.speaker === 'string' && attrs.speaker) {
    bullet.speaker = attrs.speaker;
  }
  if (typeof attrs.ambiguityFlag === 'string' && attrs.ambiguityFlag) {
    bullet.ambiguityFlag = attrs.ambiguityFlag;
  }
  return bullet;
}

function topicFromNode(n: PMNode): Topic | null {
  if (n.type !== 'topic' || !Array.isArray(n.content)) return null;

  const titleNode = findChild(n, 'topicTitle');
  const title = nodeText(titleNode).trim();

  const bullets: Bullet[] = [];
  for (const child of n.content) {
    if (child.type !== 'bullet') continue;
    const b = bulletFromNode(child);
    if (b) bullets.push(b);
  }

  if (!title && bullets.length === 0) return null;

  return {
    title: title || 'Untitled topic',
    kind: topicKindFromAttr(n.attrs?.key),
    bullets,
  };
}

/** Parse an attendeeGroup PM node into an AttendeeGroup. Returns
 *  null for groups with both firm and members empty (the user
 *  cleared everything → drop the row). */
function attendeeGroupFromNode(n: PMNode): AttendeeGroup | null {
  if (n.type !== 'attendeeGroup') return null;
  const firmNode = findChild(n, 'firmText');
  const membersNode = findChild(n, 'membersText');
  const firm = nodeText(firmNode).trim();
  const membersRaw = nodeText(membersNode).trim();
  // Split on `;` — `,` is reserved for credentials inside a single
  // member entry ("Andrew Cridlin, ASLA"). The OJB convention is
  // semicolons between members, commas between a member's
  // credentials. Splitting on commas here turned each credential
  // into its own pill, which is the bug we're fixing.
  const members = membersRaw
    ? membersRaw
        .split(';')
        .map((m) => m.trim())
        .filter((m) => m.length > 0)
    : [];

  if (!firm && members.length === 0) return null;

  const isOjb = n.attrs?.isOjb === true;
  return {
    firm: firm || (isOjb ? 'OJB' : 'External'),
    isOjb,
    members,
  };
}

/** Parse an actionItem PM node into an ActionItem. Returns null
 *  for empty items (no description, no attrs worth keeping). */
function actionItemFromNode(n: PMNode): ActionItem | null {
  if (n.type !== 'actionItem') return null;
  const description = nodeText(n).trim();
  if (!description) return null;

  const attrs = n.attrs ?? {};
  const item: ActionItem = {
    description,
    status: statusFromAttr(attrs.status),
  };
  if (typeof attrs.assigneeName === 'string' && attrs.assigneeName) {
    item.assigneeName = attrs.assigneeName;
  }
  if (typeof attrs.dueDate === 'string' && attrs.dueDate) {
    item.dueDate = attrs.dueDate;
  }
  return item;
}

/**
 * Walk a Tiptap doc JSON and produce a StructuredContent.
 *
 * @param doc       The editor's getJSON() output (root `doc` node)
 * @param fallback  Last-known-good structured content. Used for
 *                  any field the doc doesn't carry (e.g. when the
 *                  meetingHeader is missing because the seed ran
 *                  on an empty meeting).
 */
export function proseMirrorDocToStructured(
  doc: PMNode,
  fallback: StructuredContent,
): StructuredContent {
  const top = doc?.content ?? [];

  let title = fallback.title;
  let project = fallback.project;
  let meetingDate = fallback.meetingDate;
  let notesDate = fallback.notesDate;
  let location = fallback.location;
  let meetingFormat = fallback.meetingFormat;
  let templateVariant = fallback.templateVariant;
  let attendeeGroups: AttendeeGroup[] = [];
  let actionItems: ActionItem[] = [];
  const topics: Topic[] = [];

  for (const node of top) {
    switch (node.type) {
      case 'meetingHeader': {
        // Title comes from the headerTitle child; meta from attrs.
        const titleNode = findChild(node, 'headerTitle');
        const titleText = nodeText(titleNode).trim();
        if (titleText) title = titleText;
        const a = node.attrs ?? {};
        if (typeof a.project === 'string') project = a.project;
        if (typeof a.meetingDate === 'string') meetingDate = a.meetingDate;
        if (typeof a.notesDate === 'string') notesDate = a.notesDate;
        if (typeof a.location === 'string') location = a.location;
        meetingFormat = meetingFormatFromAttr(a.meetingFormat);
        templateVariant = templateVariantFromAttr(a.templateVariant);
        break;
      }
      case 'attendeesBlock': {
        const groups: AttendeeGroup[] = [];
        for (const child of node.content ?? []) {
          const g = attendeeGroupFromNode(child);
          if (g) groups.push(g);
        }
        attendeeGroups = groups;
        break;
      }
      case 'actionItemsBlock': {
        const items: ActionItem[] = [];
        for (const child of node.content ?? []) {
          const it = actionItemFromNode(child);
          if (it) items.push(it);
        }
        actionItems = items;
        break;
      }
      case 'topic': {
        const t = topicFromNode(node);
        if (t) topics.push(t);
        break;
      }
      default:
        // Free paragraphs / headings the user might create via
        // StarterKit commands aren't part of the schema. Drop them
        // silently; Phase 4+ will decide if they should map into
        // topic.bullets implicitly.
        break;
    }
  }

  const out: StructuredContent = {
    title,
    project,
    meetingDate,
    notesDate,
    meetingFormat,
    templateVariant,
    attendeeGroups,
    topics,
    actionItems,
  };
  if (location && location.length > 0) out.location = location;
  return out;
}
